import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  useOutsideClick,
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'

export type ComboBoxOption = { label: string; value: string }
export interface ComboBoxProps {
  options: ComboBoxOption[]
  onSelect: (option: ComboBoxOption) => void
  preSelected?: ComboBoxOption
  placeholder?: string
  width?: string | number
}
const ComboBox = ({
  options,
  preSelected,
  placeholder = 'Type to search',
  onSelect,
  width = '100%', // default width
}: ComboBoxProps) => {
  const [selectedLabel, setSelectedLabel] = useState<string>(
    preSelected ? preSelected.label : '',
  )
  const [inputValue, setInputValue] = useState<string>(selectedLabel)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const filteredOptions = useMemo(
    () => filterOptions(options, searchQuery),
    [options, searchQuery],
  )

  const openDropdown = useCallback(() => {
    setIsDropdownVisible(true)
  }, [])

  const hideDropdown = useCallback(() => {
    setIsDropdownVisible(false)
    setActiveIndex(-1)
  }, [])

  const abandonSearch = useCallback(() => {
    hideDropdown()
    setSearchQuery('')
    setInputValue(selectedLabel)
  }, [hideDropdown, selectedLabel])

  const handleInputChange = useCallback(
    event => {
      const newValue = event.target.value
      setInputValue(newValue)
      setSearchQuery(newValue)
      setActiveIndex(-1)
      openDropdown()
    },
    [openDropdown],
  )

  const handleSelectOption = useCallback(
    option => {
      setSelectedLabel(option.label)
      setInputValue(option.label)
      setSearchQuery('')
      hideDropdown()
      if (onSelect) {
        onSelect(option)
      }
    },
    [onSelect, hideDropdown],
  )

  const toggleDropdown = useCallback(() => {
    if (isDropdownVisible) {
      hideDropdown()
    } else {
      openDropdown()
    }
  }, [isDropdownVisible, hideDropdown, openDropdown])

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        openDropdown()

        if (e.key === 'ArrowDown') {
          setActiveIndex(prevIndex =>
            prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : prevIndex,
          )
        } else {
          setActiveIndex(prevIndex =>
            prevIndex > 0 ? prevIndex - 1 : prevIndex,
          )
        }
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        handleSelectOption(filteredOptions[activeIndex])
      } else if (e.key === 'Escape' || e.key === 'Tab') {
        abandonSearch()
      }
    },
    [filteredOptions, activeIndex, handleSelectOption, openDropdown, abandonSearch],
  )

  // useState only reads preSelected once, so a selection that arrives after mount — restored from
  // storage, or chosen for the user when there is only one option — left the input showing its
  // placeholder while the rest of the page acted on the selection. Keyed on the option's own
  // fields, not the object, so a caller re-deriving it with .find() on every render does not
  // clobber what the user is typing.
  const preSelectedLabel = preSelected?.label

  useEffect(() => {
    setSelectedLabel(preSelectedLabel ?? '')
    setInputValue(preSelectedLabel ?? '')
  }, [preSelectedLabel])

  useEffect(() => {
    if (activeIndex !== -1 && listRef.current) {
      const listItem = listRef.current?.children[activeIndex]
      listItem &&
        listItem.scrollIntoView({ behavior: 'auto', block: 'nearest' })
    }
  }, [activeIndex])

  useOutsideClick({
    ref: containerRef,
    handler: () => {
      if (isDropdownVisible || inputValue !== selectedLabel) {
        abandonSearch()
      }
    },
  })

  const handleInputFocus = e => {
    e.currentTarget.select()
    openDropdown()
  }

  const removeBorder = {
    border: 'none',
    borderBottom: '1px solid',
    borderBottomColor: 'brand.lightGray',
    borderRadius: 0,
    outline: 0,
    '&:focus': {
      borderBottom: '1px solid',
      borderBottomColor: 'brand.blue',
    },
  }

  return (
    <Box
      position="relative"
      w={width}
      ref={containerRef}>
      <InputGroup>
        <Input
          w="100%"
          fontSize="20px"
          fontWeight="bold"
          border="none"
          borderBottom="1px solid"
          borderBottomColor="brand.lightGray"
          borderRadius={0}
          outline={0}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onClick={openDropdown}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          padding="8px 0"
          _focus={{
            ...removeBorder,
          }}
          _focusVisible={{
            ...removeBorder,
          }}
          _active={{
            ...removeBorder,
          }}
        />
        <InputRightElement
          width="28px"
          cursor="pointer"
          role="button"
          aria-label="Toggle options"
          onClick={toggleDropdown}>
          {isDropdownVisible ? (
            <ChevronUpIcon fontSize="20px" />
          ) : (
            <ChevronDownIcon fontSize="20px" />
          )}
        </InputRightElement>
      </InputGroup>
      {isDropdownVisible && filteredOptions.length > 0 && (
        <List
          position="absolute"
          width="100%"
          maxH="300px"
          overflowY="auto"
          bg="white"
          borderRadius="md"
          zIndex={1}
          marginTop="2px"
          p={2}
          fontSize={16}
          mb={4}
          ref={listRef}>
          <>
            {filteredOptions.map((option, index) => (
              <ListItem
                borderRadius={4}
                key={option.value}
                onClick={() => handleSelectOption(option)}
                cursor="pointer"
                _hover={{ bg: 'gray.100' }}
                bg={index === activeIndex ? 'gray.100' : 'white'}
                padding="8px">
                {highlightMatch(searchQuery, option.label)}
              </ListItem>
            ))}
          </>
        </List>
      )}
    </Box>
  )
}

const toSearchTerms = (searchValue: string) =>
  searchValue.toLowerCase().split(/\s+/).filter(Boolean)

const filterOptions = (options: ComboBoxOption[], searchValue: string) => {
  const terms = toSearchTerms(searchValue)
  if (terms.length === 0) {
    return options
  }
  return options.filter(option => {
    const label = option.label.toLowerCase()
    return terms.every(term => label.includes(term))
  })
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const highlightMatch = (searchValue: string, text: string) => {
  const terms = toSearchTerms(searchValue)
  if (terms.length === 0) {
    return text
  }
  const matcher = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi')
  return (
    <>
      {text.split(matcher).map((part, index) =>
        index % 2 === 1 ? <strong key={index}>{part}</strong> : part,
      )}
    </>
  )
}

export { ComboBox }
